import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import type { Account } from "@/hooks/useAccounts";
import { getErrorMessage } from "@/lib/errors";

interface TransferModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: Account[];
  onTransfer: (fromType: string, toType: string, amount: number) => Promise<void>;
  preselectedFrom?: string;
}

const ALLOWED_TRANSFERS: Record<string, string[]> = {
  checking: ["savings", "emergency_fund", "roth_ira", "investment"],
  savings: ["checking", "emergency_fund"],
  emergency_fund: ["checking"],
};

const CONFIRMATION_REQUIRED = ["emergency_fund"];

const TransferModal = ({ open, onOpenChange, accounts, onTransfer, preselectedFrom }: TransferModalProps) => {
  const [fromType, setFromType] = useState(preselectedFrom || "checking");
  const [toType, setToType] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  const activeAccounts = accounts.filter((a) => a.status === "active");
  const fromAccount = accounts.find((a) => a.account_type === fromType);
  const allowedDestinations = ALLOWED_TRANSFERS[fromType] || [];
  const destinationAccounts = activeAccounts.filter(
    (a) => allowedDestinations.includes(a.account_type) && a.account_type !== fromType
  );

  const handleSubmit = async () => {
    const amt = parseInt(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!toType) { toast.error("Select a destination"); return; }
    if (fromAccount && amt > fromAccount.balance) { toast.error("Insufficient balance"); return; }

    // Confirmation for emergency fund withdrawals
    if (CONFIRMATION_REQUIRED.includes(fromType) && !confirmStep) {
      setConfirmStep(true);
      return;
    }

    setLoading(true);
    try {
      await onTransfer(fromType, toType, amt);
      toast.success(`$${amt} transferred successfully`);
      onOpenChange(false);
      setAmount("");
      setToType("");
      setConfirmStep(false);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (v: boolean) => {
    onOpenChange(v);
    if (!v) { setConfirmStep(false); setAmount(""); setToType(""); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Transfer Money</DialogTitle>
        </DialogHeader>

        {confirmStep ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Withdraw from Emergency Fund?
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  This fund protects you from surprises. Are you sure you want to withdraw ${amount}?
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmStep(false)}>
                Cancel
              </Button>
              <Button variant="destructive" className="flex-1" onClick={handleSubmit} disabled={loading}>
                {loading ? "Processing..." : "Confirm Withdrawal"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">From</label>
              <Select value={fromType} onValueChange={(v) => { setFromType(v); setToType(""); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeAccounts
                    .filter((a) => Object.keys(ALLOWED_TRANSFERS).includes(a.account_type))
                    .map((a) => (
                      <SelectItem key={a.account_type} value={a.account_type}>
                        {a.account_name} — ${a.balance.toLocaleString()}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-center">
              <ArrowDown className="w-4 h-4 text-muted-foreground" />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">To</label>
              <Select value={toType} onValueChange={setToType}>
                <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                <SelectContent>
                  {destinationAccounts.map((a) => (
                    <SelectItem key={a.account_type} value={a.account_type}>
                      {a.account_name} — ${a.balance.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-7"
                  placeholder="0"
                  min={1}
                  max={fromAccount?.balance || 0}
                />
              </div>
              {fromAccount && (
                <p className="text-xs text-muted-foreground mt-1">
                  Available: ${fromAccount.balance.toLocaleString()}
                </p>
              )}
            </div>

            <Button onClick={handleSubmit} disabled={loading} className="w-full">
              {loading ? "Processing..." : "Transfer"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransferModal;
